import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Search, Globe, X, CheckCircle, Package, Users, ShoppingCart, Receipt, Truck, Command } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI, searchAPI } from '../../services/api';

const typeConfig = {
  product: { icon: Package, color: '#24549c', label: 'Product' },
  customer: { icon: Users, color: '#0d6b3e', label: 'Customer' },
  purchase_order: { icon: ShoppingCart, color: '#E65100', label: 'PO' },
  order_confirmation: { icon: Receipt, color: '#7B1FA2', label: 'OC' },
  dispatch: { icon: Truck, color: '#1565C0', label: 'Dispatch' },
};

const Header = ({ user }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Global Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcut: Cmd+K / Ctrl+K to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Click outside to close
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await searchAPI.search(searchQuery);
        setSearchResults(data.results || []);
        setSelectedIdx(-1);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  const handleResultClick = useCallback((result) => {
    navigate(result.path);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [navigate]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && selectedIdx >= 0 && searchResults[selectedIdx]) {
      handleResultClick(searchResults[selectedIdx]);
    }
  };

  const fetchNotifications = async () => {
    try {
      const data = await notificationAPI.getAll();
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await notificationAPI.markAsRead(notifId);
      setNotifications(notifications.map(n => 
        n.id === notifId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <header className="h-14 bg-white border-b border-gray-100 flex items-center justify-between px-6 flex-shrink-0">
      <div className="flex items-center gap-3" ref={searchRef}>
        <div className="relative">
          <div
            onClick={() => { setSearchOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            className={`relative flex items-center gap-2 cursor-pointer transition-all ${searchOpen ? 'w-80' : 'w-56'}`}
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <input
              ref={inputRef}
              data-testid="global-search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search across ERP..."
              className="pl-9 pr-16 py-1.5 text-xs border border-gray-200 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-gray-50 transition-all"
            />
            {!searchOpen && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-medium text-gray-400 bg-gray-100 rounded border border-gray-200">
                <Command className="w-2.5 h-2.5" />K
              </span>
            )}
            {searchOpen && searchQuery && (
              <button onClick={(e) => { e.stopPropagation(); setSearchQuery(''); setSearchResults([]); inputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200">
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {searchOpen && (searchQuery.length >= 2 || searchResults.length > 0) && (
            <div data-testid="global-search-results" className="absolute top-full left-0 mt-1.5 w-96 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-80 overflow-y-auto">
              {searching ? (
                <div className="p-4 text-center text-xs text-gray-400">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-1.5" />
                  Searching...
                </div>
              ) : searchResults.length === 0 && searchQuery.length >= 2 ? (
                <div className="p-4 text-center">
                  <Search className="w-6 h-6 text-gray-300 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">No results for "{searchQuery}"</p>
                </div>
              ) : (
                <div className="py-1">
                  <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{searchResults.length} results</p>
                  {searchResults.map((r, i) => {
                    const config = typeConfig[r.type] || typeConfig.product;
                    const Icon = config.icon;
                    return (
                      <button
                        key={`${r.type}-${r.id}`}
                        data-testid={`search-result-${r.type}-${i}`}
                        onClick={() => handleResultClick(r)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${i === selectedIdx ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: config.color + '12' }}>
                          <Icon className="w-3.5 h-3.5" style={{ color: config.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-900 truncate">{r.label}</p>
                          <p className="text-[10px] text-gray-400 truncate">{r.sub}</p>
                        </div>
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: config.color + '12', color: config.color }}>
                          {config.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
          <Globe className="w-3.5 h-3.5" style={{ color: '#24549c' }} />
          <span className="font-medium">{user.branch || 'All Branches'}</span>
        </div>
        
        {/* Notification Bell */}
        <div className="relative">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="notification-bell"
          >
            <Bell className="w-4 h-4 text-gray-500" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center" style={{ backgroundColor: '#e65100' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-96 overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 p-3 flex items-center justify-between">
                <h3 className="text-xs font-bold text-gray-900">Notifications</h3>
                <button onClick={() => setShowNotifications(false)} className="p-1 rounded hover:bg-gray-100">
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>

              {notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {notifications.map(notif => (
                    <div 
                      key={notif.id}
                      onClick={() => markAsRead(notif.id)}
                      className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notif.is_read ? 'bg-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          !notif.is_read ? 'bg-blue-600' : 'bg-gray-200'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs font-semibold text-gray-900 truncate">{notif.title}</p>
                            <span className="text-[10px] text-gray-400 flex-shrink-0">
                              {formatTime(notif.created_at)}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{notif.message}</p>
                          {notif.type === 'overdue_po' && (
                            <span className="inline-flex mt-1.5 px-2 py-0.5 text-[9px] font-semibold rounded-full bg-orange-100 text-orange-700">
                              Overdue Delivery
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: '#24549c' }}>
            {user.name?.split(' ').map((n) => n[0]).join('').substring(0, 2)}
          </div>
          <div className="hidden sm:block">
            <p className="text-xs font-semibold text-gray-900 leading-tight">{user.name}</p>
            <p className="text-[10px] text-gray-400">{user.role}</p>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
